import { getSettings } from '../storage/settings';
import { getTrackById } from './tracks';
import { getAudioCtx, resumeAudioCtx } from './ctx';

export class GameAudio {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private bgmGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;

  // BGM state
  private bgmSource: AudioBufferSourceNode | null = null;
  private bgmBuffer: AudioBuffer | null = null;
  private currentTrackId = '';
  private bgmLoading = false;

  // SFX buffers
  private sfxBuffers: Map<string, AudioBuffer> = new Map();

  // Throttle collision sounds
  private lastCollisionTime = 0;

  private destroyed = false;

  init() {
    if (this.ctx || !getSettings().soundEnabled) return;

    // Resume the shared context (must be called inside a user gesture)
    resumeAudioCtx();

    const ctx = getAudioCtx();
    if (!ctx) return;
    this.ctx = ctx;

    this.masterGain = ctx.createGain();
    this.masterGain.gain.value = 0.85;
    this.masterGain.connect(ctx.destination);

    this.bgmGain = ctx.createGain();
    this.bgmGain.gain.value = 0.55;
    this.bgmGain.connect(this.masterGain);

    this.sfxGain = ctx.createGain();
    this.sfxGain.gain.value = 0.75;
    this.sfxGain.connect(this.masterGain);

    void this.preloadSfx();
  }

  private async preloadSfx() {
    const files: Record<string, string> = {
      goal:      '/assets/audio/sfx/goal.mp3',
      pop:       '/assets/audio/sfx/explosion.mp3',
      bubble:    '/assets/audio/sfx/ping.mp3',
      collision: '/assets/audio/sfx/blaster.mp3',
    };
    await Promise.allSettled(
      Object.entries(files).map(async ([key, url]) => {
        const buf = await this.fetchAudioBuffer(url);
        if (buf) this.sfxBuffers.set(key, buf);
      }),
    );
  }

  private async fetchAudioBuffer(url: string): Promise<AudioBuffer | null> {
    if (!this.ctx) return null;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const raw = await res.arrayBuffer();
      return await this.ctx.decodeAudioData(raw);
    } catch {
      return null;
    }
  }

  async loadBgm(trackId: string) {
    if (!this.ctx || trackId === this.currentTrackId || this.bgmLoading) return;
    const track = getTrackById(trackId);
    if (!track) return;

    this.bgmLoading = true;
    const buf = await this.fetchAudioBuffer(track.file);
    this.bgmLoading = false;

    if (!buf || this.destroyed || !this.ctx) return;
    this.bgmBuffer = buf;
    this.currentTrackId = trackId;
    this.startBgmLoop();
  }

  private startBgmLoop() {
    if (!this.ctx || !this.bgmBuffer || !this.bgmGain) return;
    this.bgmSource?.stop();
    this.bgmSource = this.ctx.createBufferSource();
    this.bgmSource.buffer = this.bgmBuffer;
    this.bgmSource.loop = true;
    this.bgmSource.connect(this.bgmGain);
    // Ensure context is running before starting source
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().then(() => this.bgmSource?.start()).catch(() => {});
    } else {
      this.bgmSource.start();
    }
  }

  setBgmVolume(tensionScale: number) {
    if (!this.bgmGain || !this.ctx) return;
    const vol = tensionScale < 0.2 ? 0.15 : 0.55;
    this.bgmGain.gain.setTargetAtTime(vol, this.ctx.currentTime, 0.25);
  }

  // ── SFX ──────────────────────────────────────────────────────────

  private playSfxBuffer(key: string, volume = 1, pitchRate = 1) {
    if (!this.ctx || !this.sfxGain) return;
    const buf = this.sfxBuffers.get(key);
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = pitchRate;
    const gain = this.ctx.createGain();
    gain.gain.value = volume;
    src.connect(gain);
    gain.connect(this.sfxGain);
    src.start();
  }

  playCollision(intensity: number) {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    if (now - this.lastCollisionTime < 0.08) return;
    this.lastCollisionTime = now;
    const clamped = Math.max(0.1, Math.min(1, intensity));
    this.playSfxBuffer('collision', clamped * 0.55, 0.8 + clamped * 0.8);
  }

  playPopper() {
    this.playSfxBuffer('pop', 0.7, 1.0 + Math.random() * 0.4);
  }

  playBubble() {
    this.playSfxBuffer('bubble', 0.65, 1.2 + Math.random() * 0.6);
  }

  playGoal(rank: number) {
    const pitches = rank === 1 ? [0.9, 1.0, 1.2, 1.5] : rank <= 3 ? [1.0, 1.3] : [1.0];
    pitches.forEach((rate, i) => {
      setTimeout(() => this.playSfxBuffer('goal', 0.7, rate), i * 80);
    });
  }

  playCountdown(n: number) {
    if (!this.ctx || !this.sfxGain) return;
    // Ensure context is running before playing
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().then(() => this._playCountdownNow(n)).catch(() => {});
    } else {
      this._playCountdownNow(n);
    }
  }

  private _playCountdownNow(n: number) {
    if (!this.ctx || !this.sfxGain) return;
    const ctx = this.ctx;
    const sfxGain = this.sfxGain;
    const now = ctx.currentTime;
    const freq = n === 0 ? 880 : 440 + n * 80;
    const duration = n === 0 ? 0.5 : 0.22;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.001, now);
    gain.gain.linearRampToValueAtTime(0.22, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.connect(gain);
    gain.connect(sfxGain);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  setRollingIntensity(_v: number) { /* no-op */ }

  destroy() {
    this.destroyed = true;
    try { this.bgmSource?.stop(); } catch { /* already stopped */ }
    // Disconnect our gains from the shared context (don't close the context itself)
    try { this.masterGain?.disconnect(); } catch { /* ignore */ }
    this.ctx = null;
    this.masterGain = null;
    this.bgmGain = null;
    this.sfxGain = null;
  }
}
